import { House } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkAction } from "../../components/Screen.js";
import { useParams } from "react-router";

import { API_ERROR, HttpError, fetchRecap, type Recap } from "../../services/http.js";
import { RecapBoard } from "./RecapBoard.js";
import { ShareLink } from "./ShareLink.js";

/**
 * What the game gives you back afterwards — `/recap/:gameId`.
 *
 * # The first page with no socket
 *
 * Every other screen in this project holds a live connection and a seat. This
 * one holds a game id out of the URL and fetches once. That is the whole point:
 * a recap is opened days later, by somebody who may not have played, on a device
 * that holds nothing — so there is no party to authenticate against, because the
 * party is over.
 *
 * The game id is the capability, the same argument `/api/clip/{sha256}` makes
 * for a digest.
 *
 * # Phase 16f-1: it had no navigation at all
 *
 * Not "not enough" — **none**. No `Link`, no `navigate`; the only interactive
 * element was `ShareLink`, which shares rather than goes anywhere. So the last
 * screen of a game was a dead end on every one of its four render paths, and
 * the way on was the browser's Back button.
 *
 * The exit is on all four, including the ones that failed. A recap that could
 * not load is *more* of a dead end than one that could, not less.
 *
 * # "Still going" is a guess, and is worded as one
 *
 * The server answers `404` for a live game **and** for one that never existed,
 * indistinguishably and on purpose: telling them apart would confirm to somebody
 * holding an old link that a party is playing right now. So this screen offers
 * both readings rather than claiming the one it cannot know.
 */
export function RecapPage() {
  const { t } = useTranslation();
  const { gameId } = useParams();
  const [recap, setRecap] = useState<Recap | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A route with no `:gameId` is not a *state* — it is a fact about this render,
  // and setting state for it in an effect is a cascading render for something
  // already known.
  const missing = !gameId || notFound;

  useEffect(() => {
    if (!gameId) {
      return;
    }
    let cancelled = false;
    void fetchRecap(gameId).then(
      (loaded) => {
        if (!cancelled) {
          setRecap(loaded);
        }
      },
      (cause: unknown) => {
        if (cancelled) {
          return;
        }
        if (cause instanceof HttpError && cause.code === API_ERROR.notFound) {
          setNotFound(true);
          return;
        }
        setError(cause instanceof Error ? cause.message : t("recap.loadFailed"));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [gameId, t]);

  if (missing) {
    return (
      <main className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-2xl font-semibold">{t("recap.missing.title")}</h1>
        <p className="text-sm text-white/60">{t("recap.missing.lead")}</p>
        <HomeLink />
      </main>
    );
  }

  if (error) {
    return (
      <main className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-2xl font-semibold">{t("recap.errorTitle")}</h1>
        <p className="text-sm text-white/60">{error}</p>
        <HomeLink />
      </main>
    );
  }

  // Three-state loading: `null` is "still loading", and an empty state that
  // flashed before the answer arrived would read as a broken screen.
  if (!recap) {
    return (
      <main className="flex min-h-full items-center justify-center p-6">
        <p className="text-sm text-white/50">{t("recap.loading")}</p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-3xl flex-col gap-6 p-5 pb-16">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-flash-500">{recap.mode}</p>
        <h1 className="mt-1 text-3xl font-semibold">{t("board.howItWent")}</h1>
        <p className="mt-1 text-sm text-white/60">
          {t("recap.rounds", { count: recap.rounds_played })}
          {recap.rounds_voided > 0 && (
            // Shown rather than hidden: a void is a deliberate do-over
            // (ADR 0009), and a recap that quietly dropped it would leave a
            // party looking for a round they remember playing.
            <> · {t("recap.voided", { count: recap.rounds_voided })}</>
          )}
        </p>
      </header>

      <RecapBoard recap={recap} />
      <ShareLink />
      <HomeLink />
    </main>
  );
}

/**
 * The way on.
 *
 * A recap is opened days later by somebody who may never have played, so the
 * destination is the front door rather than "back to the party" — the party is
 * over, which is the only reason this page renders at all.
 *
 * A real `Link`, not a `navigate` in an `onClick`: it is a navigation, so it
 * should be middle-clickable and it should say where it goes on hover.
 */
function HomeLink() {
  const { t } = useTranslation();
  return (
    <div className="w-full max-w-xs self-center">
      <LinkAction to="/" icon={House}>
        {t("recap.home")}
      </LinkAction>
    </div>
  );
}
